// streakService.js
// Streak Mode logic, mirroring StreakLogic.swift / StreakModels.swift.
// Reuses playerService.fetchPlayersForSport for the per-sport player pool, so
// streaks and drafts share the same cached player data.
//
// Streaks are UNLIMITED plays. Local stats (best / games played / today's best)
// are kept in localStorage for the Home card and the overview screen. Firestore
// persistence + the streak leaderboard are wired in a follow-up batch.

import { fetchPlayersForSport } from "./playerService";

export { fetchPlayersForSport };

// ─── Random player draw ───────────────────────────────────────────────────────
// Pick `count` random players across ALL positions for the sport, de-duplicated
// by name and excluding any names already shown this game. Mirrors
// getRandomPlayersForSport(count:excluding:) in StreakLogic.swift.

export function getRandomPlayers(allPlayers, count, excludedNames = new Set()) {
  const eligible = allPlayers.filter(p => !excludedNames.has(p.name));
  // Fallback: if exclusions drain the pool, allow the full pool again.
  const source = eligible.length >= count ? eligible : allPlayers;

  const shuffled = [...source].sort(() => Math.random() - 0.5);
  const seen = new Set();
  const out = [];
  for (const p of shuffled) {
    if (seen.has(p.name)) continue;   // never two of the same player in one set
    seen.add(p.name);
    out.push(p);
    if (out.length === count) break;
  }
  return out;
}

// ─── Score quality ─────────────────────────────────────────────────────────────
// Thresholds + colors mirror StreakScoreQuality in StreakModels.swift.

export function streakQuality(streak) {
  if (streak <= 2)  return { label: "Getting Started", color: "#9aa0a6" };
  if (streak <= 5)  return { label: "Decent Run",      color: "#4a90e2" };
  if (streak <= 9)  return { label: "Good Streak",     color: "#34c759" };
  if (streak <= 15) return { label: "Excellent!",      color: "#ff9500" };
  if (streak <= 25) return { label: "Outstanding!",    color: "#ff3b30" };
  return { label: "LEGENDARY!", color: "#d4f53c" };
}

// ─── Local stats ────────────────────────────────────────────────────────────────
// Per owner (uid or "guest") + sport. todaysBest resets when the date rolls over.

const STREAK_STATS_PREFIX = "rosterable_streak_stats";

function statsKey(ownerId, sportId) {
  return `${STREAK_STATS_PREFIX}_${ownerId}_${sportId}`;
}

export function getStreakStats(ownerId, sportId) {
  const empty = { allTimeBest: 0, gamesPlayed: 0, todaysBest: 0 };
  try {
    const raw = localStorage.getItem(statsKey(ownerId, sportId));
    if (!raw) return empty;
    const s = JSON.parse(raw);
    const today = new Date().toDateString();
    return {
      allTimeBest: s.allTimeBest || 0,
      gamesPlayed: s.gamesPlayed || 0,
      todaysBest: s.todayDate === today ? (s.todaysBest || 0) : 0,
    };
  } catch {
    return empty;
  }
}

export function recordStreakResult(ownerId, sportId, finalScore) {
  try {
    const today = new Date().toDateString();
    let prev = {};
    try { prev = JSON.parse(localStorage.getItem(statsKey(ownerId, sportId))) || {}; } catch { /* none */ }
    const sameDay = prev.todayDate === today;
    const next = {
      allTimeBest: Math.max(prev.allTimeBest || 0, finalScore),
      gamesPlayed: (prev.gamesPlayed || 0) + 1,
      todaysBest: Math.max(sameDay ? (prev.todaysBest || 0) : 0, finalScore),
      todayDate: today,
    };
    localStorage.setItem(statsKey(ownerId, sportId), JSON.stringify(next));
    return next;
  } catch {
    return null;
  }
}
